import {
    feedbackPreamble,
    summarizeFeedback,
    explainFeedback,
    whatDoYouThinkScreenshot,
    categorizeFeedback,
    feedbackCategoryBugReport,
    feedbackCategoryFeatureRequest,
    feedbackCategoryCompliment,
} from './FeedbackForm.locstring.json';
import { sendFeedback } from 'owa-locstrings/lib/strings/sendfeedback.locstring.json';
import loc from 'owa-localize';
import { ControlIcons } from 'owa-control-icons';
import setFeedbackMessage from '../actions/setFeedbackMessage';
import setFeedbackSubject from '../actions/setFeedbackSubject';
import setClassificationState from '../actions/setClassificationState';
import sendFeedbackMessage from '../actions/sendFeedbackMessage';
import { MAX_SUBJ_LENGTH } from '../FeedbackConstants';

import { IconButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { ChoiceGroup } from 'office-ui-fabric-react/lib/ChoiceGroup';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { observer } from 'mobx-react';
import {
    setScreenshotImageFromFileList,
    setScreenshotFileState,
} from '../actions/setScreenshotImageActions';
import { store } from '../store/store';
import {
    isBrowserIE,
    isBrowserSafari,
    isBrowserChrome,
    isChromiumOs,
} from 'owa-user-agent/lib/userAgent';
import { isMinimumBrowserVersion } from 'owa-user-agent';
import { keydownHandler } from 'owa-hotkeys';
import * as React from 'react';
import FeedbackAreaDropdown from './FeedbackAreaDropdown';

let styles = require('./FeedbackForm.scss');

function isChromeUnder64OnChromiumOS() {
    return isChromiumOs() && isBrowserChrome() && !isMinimumBrowserVersion([64]);
}

// Paste Screen shot unsupported browsers issues:
// 1. All IE::
// VSO:19173 - [feedback tool] investigate why onpaste handler is not called for IE on paste images to textarea
// 2. All Mac Safari:
// VSO:19175 - [feedback tool] investigate why onpaste handler for textareas is not working for image in Mac Safari browser
// 3. Chromium OS under Chrome 64
// It a bug that clipboard will not work for the image paste for screen shot and fixed in Chrome 64
// https://bugs.chromium.org/p/chromium/issues/detail?id=675050
const doesBrowserSupportPastingImageToTextAreas =
    !isBrowserIE() && !isBrowserSafari() && !isChromeUnder64OnChromiumOS();

export interface FeedbackFormProps {
    onSendFeedback?: () => void;
}

export interface FeedbackFormState {
    subject: string;
}

@observer
export default class FeedbackForm extends React.Component<FeedbackFormProps, FeedbackFormState> {
    constructor(props) {
        super(props);
        this.state = { subject: store.subject };
    }

    private onInputSubject = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue: any
    ) => {
        if (!newValue || newValue.length <= MAX_SUBJ_LENGTH) {
            setFeedbackSubject(store, newValue);
            this.setState({ subject: newValue });
        } else {
            // This block should NOT be necessary, but there's currently a bug (#1350) where a controlled
            // TextField will continue to accept input even if its `value` prop isn't updated.
            // (The correct behavior is that the displayed value should *always* match the `value` prop.
            // If the `value` prop isn't updated in response to user input, the input should be ignored.)
            // Because this is a large behavior change, the bug won't be fixed until Fabric 7.
            // As a workaround, force re-rendering with the existing value.
            this.setState({ subject: this.state.subject });
        }
    };

    private onInputMessage(
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue: any
    ) {
        setFeedbackMessage(store, newValue);
    }

    private onPaste(event) {
        let items: FileList = (event.clipboardData || event.originalEvent.clipboardData).items;
        setScreenshotImageFromFileList(store, items);
    }

    private classify(ev: React.FormEvent<HTMLInputElement>, option: any) {
        setClassificationState(store, option.key);
    }

    private removeScreenshot() {
        setScreenshotFileState(store, null, null);
    }

    private renderScreenshot = (screenshotFileBase64: string) => {
        return (
            <div className={styles.screenshotContainer}>
                <img className={styles.screenshot} src={screenshotFileBase64} />
                <IconButton
                    className={styles.removeScreenshot}
                    onClick={this.removeScreenshot}
                    iconProps={{
                        iconName: ControlIcons.Cancel,
                    }}
                />
            </div>
        );
    };

    render() {
        let { message, screenshotBlob, screenshotFileBase64 } = store;

        return (
            <div className={styles.menuFeedback}>
                <span className={styles.smallText}>{loc(feedbackPreamble)}</span>
                <TextField
                    value={this.state.subject}
                    onChange={this.onInputSubject}
                    styles={{ root: styles.marginTopBottom }}
                    label={loc(summarizeFeedback)}
                    required={true}
                />
                <TextField
                    multiline={true}
                    value={message}
                    onChange={this.onInputMessage}
                    onPaste={this.onPaste}
                    styles={{
                        field: styles.textArea,
                        root: styles.marginTopBottom,
                        description: styles.smallText,
                    }}
                    label={loc(explainFeedback)}
                    description={
                        doesBrowserSupportPastingImageToTextAreas &&
                        !screenshotBlob &&
                        !screenshotFileBase64
                            ? loc(whatDoYouThinkScreenshot)
                            : null
                    }
                />
                {screenshotBlob &&
                    screenshotFileBase64 &&
                    this.renderScreenshot(screenshotFileBase64)}
                <ChoiceGroup
                    styles={{ root: styles.marginTopBottom }}
                    label={loc(categorizeFeedback)}
                    options={[
                        { key: '🐞', text: '🐞 ' + loc(feedbackCategoryBugReport) },
                        { key: '🙏', text: '🙏 ' + loc(feedbackCategoryFeatureRequest) },
                        { key: '💖', text: '💖 ' + loc(feedbackCategoryCompliment) },
                    ]}
                    onChange={this.classify}
                />
                <FeedbackAreaDropdown />
                <PrimaryButton
                    onClick={this.onSend}
                    className={styles.sendButton}
                    text={loc(sendFeedback)}
                />
            </div>
        );
    }

    @keydownHandler({
        hotmail: 'ctrl+enter',
        yahoo: 'ctrl+enter',
        gmail: 'ctrl+enter',
        owa: 'ctrl+enter',
    })
    sendFeedbackKeyboardShortcut() {
        this.onSend();
    }

    private onSend = () => {
        sendFeedbackMessage();

        if (this.props.onSendFeedback) {
            this.props.onSendFeedback();
        }
    };
}
